import React from 'react';
import withStyles from 'isomorphic-style-loader/withStyles';
import { useSelector } from 'react-redux';
import Link from '../../components/Link';
/* Images */
import Visa from '/public/Icons/visa.svg';
import MasterCard from '/public/Icons/masterCard.svg';
import AmericanExpress from '/public/Icons/american-express.svg';
import Discover from '/public/Icons/discover.svg';
import FaceBookIcon from '/public/Icons/faceBookIcon.svg';
import InstagramIcon from '/public/Icons/instagramIcon.svg';
import TwitterIcon from '/public/Icons/twitterIcon.svg';
import YouTubeIcon from '/public/Icons/youtubeIcon.svg';
import s from './Footer.css';

// Big-platform footer — fully hand-built so every important page is reachable (bypasses the thin admin
// footer config). Internal links use <Link> for client-side navigation; legal pages are the premium
// hand-built routes (see src/routes/legal).
const COLUMNS = [
  {
    title: 'Company',
    links: [
      ['About BestHand', '/about-besthand'],
      ['How it works', '/how-it-works'],
      ['Trust & Safety', '/trust-safety'],
      ['Pricing', '/pricing'],
      ['For Business', '/business'],
      ['Careers', '/careers'],
      ['Guides & home care', '/guides'],
      ['For property managers', '/property-managers'],
      ['Contact us', '/contact-us'],
    ],
  },
  {
    title: 'Services',
    links: [
      ['Plumbing', '/services/plumbing'],
      ['Electrical', '/services/electrical-help'],
      ['Cleaning', '/services/cleaning'],
      ['Moving help', '/services/moving-help'],
      ['TV mounting', '/services/tv-mounting'],
      ['All Services', '/services'],
      ['Browse all services', '/category?type=all'],
    ],
  },
  {
    title: 'For Pros',
    links: [
      ['Become a Pro', '/become-a-pro'],
      ['Become a Company', '/become-a-company'],
      ['How it works', '/how-it-works'],
      ['Background checks', '/background-check'],
      ['Independent contractor', '/independent-contractor'],
    ],
  },
  {
    title: 'Support',
    links: [
      ['Help Center', '/help'],
      ['Help & FAQ', '/faq'],
      ['Reviews', '/reviews'],
      ['Gift cards', '/gift-cards'],
      ['Peace of Mind Protection', '/trust-safety'],
      ['Refunds & cancellations', '/pricing'],
      ['Leave a review', '/leave-a-review'],
      ['Contact support', '/contact-us'],
    ],
  },
  {
    title: 'Legal',
    links: [
      ['Terms of Service', '/terms'],
      ['Privacy Policy', '/privacy-policy'],
      // CCPA/CPRA — required footer disclosure + opt-out (public).
      ['Do Not Sell or Share My Personal Information', '/do-not-sell'],
      // CCPA/CPRA — access / correct / delete self-service (auth-gated).
      ['Your privacy & data rights', '/data-rights'],
      ['SMS / text terms', '/sms-consent'],
      ['Background Check Policy', '/background-check'],
      // A11Y: WCAG 2.1 AA accessibility statement.
      ['Accessibility', '/accessibility'],
    ],
  },
];

// SEO internal-linking band — sitewide links into our priority metro service pages
// (Philadelphia / Jersey City / New York). Boosts crawl discovery + passes authority
// from every page so these metro pages climb from page 3-4 toward page 1. Service
// slugs + city slugs verified live-published in SeoLandingPage before shipping.
const SEO_METROS = [
  ['Philadelphia', 'philadelphia-pa'],
  ['Jersey City', 'jersey-city-nj'],
  ['New York', 'new-york-ny'],
];
const SEO_SERVICES = [
  ['House Cleaning', 'cleaning'],
  ['TV Mounting', 'tv-mounting'],
  ['Plumbing', 'plumbing'],
  ['Electrical', 'electrical'],
  ['HVAC & A/C', 'hvac'],
  ['Painting', 'painting'],
  ['Furniture Assembly', 'furniture-assembly'],
  ['Yard Work', 'yard-work'],
  ['Pressure Washing', 'pressure-washing'],
  ['Junk Removal', 'junk-removal'],
  ['Moving Help', 'moving-help'],
  ['Smart Home', 'smart-home-services'],
];

const CLIENT_APP_STORE = 'https://apps.apple.com/us/app/besthand/id6759716088';
const CLIENT_PLAY_STORE =
  'https://play.google.com/store/apps/details?id=com.worldtechnologiessolutionsllc.besthand&hl=en_US';

function Footer({ isBottomBanner }) {
  // LARGE PROJECTS FLAG: hide the LP "Become a Company" footer link when OFF.
  // Read from the SSR-seeded runtime store (fail-closed: undefined -> hidden).
  const largeProjectsEnabled = useSelector((state) => state?.runtime?.largeProjectsEnabled);
  const youtubeLink = useSelector((state) => state?.siteSettings?.data?.youtubeLink);
  const twitterLink = useSelector((state) => state?.siteSettings?.data?.twitterLink);
  const facebookLink = useSelector((state) => state?.siteSettings?.data?.facebookLink);
  const instagramLink = useSelector((state) => state?.siteSettings?.data?.instagramLink);
  const year = new Date().getFullYear();

  const socials = [
    [facebookLink, FaceBookIcon, 'Facebook'],
    [instagramLink, InstagramIcon, 'Instagram'],
    [twitterLink, TwitterIcon, 'Twitter'],
    [youtubeLink, YouTubeIcon, 'YouTube'],
  ].filter(([url]) => url);

  return (
    <footer className={`${s.footerBox}${isBottomBanner ? ` ${s.bottomSpace}` : ''}`}>
      <div className={s.footerInner}>
        {/* Top: brand + link columns */}
        <div className={s.footerTop}>
          <div className={s.brandCol}>
            <Link to="/" className={s.brand}>BestHand</Link>
            <p className={s.brandTag}>Trusted local pros, booked in minutes — backed by secure payments and a satisfaction commitment.</p>
            <div className={s.appLinks}>
              <a className={s.appLink} href={CLIENT_APP_STORE} target="_blank" rel="noopener noreferrer">App Store</a>
              <a className={s.appLink} href={CLIENT_PLAY_STORE} target="_blank" rel="noopener noreferrer">Google Play</a>
            </div>
            {socials.length > 0 && (
              <ul className={s.socialRow}>
                {socials.map(([url, Icon, label]) => (
                  <li key={label}>
                    <a href={url} target="_blank" rel="noopener noreferrer" aria-label={label}>
                      <img src={Icon} alt={label} />
                    </a>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {COLUMNS.map((col) => (
            <nav className={s.linkCol} key={col.title} aria-label={col.title}>
              <h3 className={s.colTitle}>{col.title}</h3>
              <ul>
                {col.links
                  // LARGE PROJECTS FLAG: omit the LP "Become a Company" link when OFF (additive filter).
                  .filter(([, to]) => largeProjectsEnabled || to !== '/become-a-company')
                  .map(([label, to]) => (
                    <li key={label}><Link to={to} className={s.footerLink}>{label}</Link></li>
                  ))}
              </ul>
            </nav>
          ))}
        </div>

        {/* SEO: sitewide internal links into priority metro service pages */}
        <div className={s.seoBand}>
          <div className={s.seoTitle}>Popular home services by city</div>
          <div className={s.seoGrid}>
            {SEO_METROS.map(([city, citySlug]) => (
              <nav className={s.linkCol} key={citySlug} aria-label={`Services in ${city}`}>
                <h3 className={s.colTitle}>{city}</h3>
                <ul>
                  {SEO_SERVICES.map(([label, slug]) => (
                    <li key={slug}>
                      <Link to={`/services/${slug}/${citySlug}`} className={s.footerLink} title={`${label} in ${city}`}>{label}</Link>
                    </li>
                  ))}
                </ul>
              </nav>
            ))}
          </div>
        </div>

        {/* Bottom: payments + Stripe + copyright */}
        <div className={s.footerBottom}>
          <div className={s.payments}>
            <img src={Visa} alt="Visa" className={s.paymentCardIcon} />
            <img src={MasterCard} alt="Mastercard" className={s.paymentCardIcon} />
            <img src={AmericanExpress} alt="American Express" className={s.paymentCardIcon} />
            <img src={Discover} alt="Discover" className={s.paymentCardIcon} />
            {/* Powered by Stripe — official S mark (Stripe purple), per Stripe brand guidelines */}
            <a className={s.poweredByStripe} href="https://stripe.com" target="_blank" rel="noopener noreferrer" aria-label="Payments powered by Stripe">
              <span className={s.pbsLabel}>Powered by</span>
              <span className={s.pbsBrand}>
                <svg className={s.pbsMark} viewBox="0 0 24 24" aria-hidden="true">
                  <path fill="#635BFF" d="M13.976 9.15c-2.172-.806-3.356-1.426-3.356-2.409 0-.831.683-1.305 1.901-1.305 2.227 0 4.515.858 6.09 1.631l.89-5.494C18.252.975 15.697 0 12.165 0 9.667 0 7.589.654 6.104 1.872 4.56 3.147 3.757 4.992 3.757 7.218c0 4.039 2.467 5.76 6.476 7.219 2.585.92 3.445 1.574 3.445 2.583 0 .98-.84 1.545-2.354 1.545-1.875 0-4.965-.921-6.99-2.109l-.9 5.555C5.175 22.99 8.385 24 11.714 24c2.641 0 4.843-.624 6.328-1.813 1.664-1.305 2.525-3.236 2.525-5.732 0-4.128-2.524-5.851-6.594-7.305h.003z" />
                </svg>
                Stripe
              </span>
            </a>
          </div>
          <div className={s.copyRight}>© {year} BestHand US LLC · All rights reserved.</div>
        </div>
      </div>
    </footer>
  );
}

export default withStyles(s)(Footer);
